import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Button, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { Link } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const products = [
  {
    title: 'Self-Managed Account',
    subtitle: 'A self-directed account for the sophisticated investor',
    features: [
      'Co-invest alongside the iAngels fund',
      '$10,000 minimum per deal',
      'Build a diversified portfolio at your pace',
      'Full visibility into every opportunity',
      'Choose which deals to invest in',
    ],
    highlight: 'From $10K per deal',
  },
  {
    title: 'iAngels Managed Account',
    subtitle: 'Hands-off access to Israel\'s top startups',
    features: [
      '$200,000 minimum commitment',
      'Guaranteed stake in every deal or criteria-based auto-allocation',
      '20+ startup diversification',
      'Professional portfolio construction',
      'Dedicated account management',
    ],
    highlight: 'From $200K',
  },
];

export default function Capital() {
  return (
    <Box>
      {/* Hero */}
      <Box sx={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', color: '#fff', py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h2" fontWeight={700} gutterBottom>Invest in Israel's Future</Typography>
          <Typography variant="h5" sx={{ opacity: 0.92 }}>
            Two ways to access the best opportunities in Israeli tech
          </Typography>
        </Container>
      </Box>

      {/* Products */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={4}>
          {products.map((p) => (
            <Grid item xs={12} md={6} key={p.title}>
              <Card elevation={2} sx={{ height: '100%', borderRadius: 3, border: '1px solid #e0f2f1', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="overline" color="#0d9488" fontWeight={700}>{p.highlight}</Typography>
                  <Typography variant="h4" fontWeight={700} gutterBottom>{p.title}</Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>{p.subtitle}</Typography>
                  <List dense sx={{ flex: 1 }}>
                    {p.features.map((f) => (
                      <ListItem key={f} disableGutters>
                        <ListItemIcon sx={{ minWidth: 36 }}><CheckCircleIcon sx={{ color: '#0d9488' }} /></ListItemIcon>
                        <ListItemText primary={f} />
                      </ListItem>
                    ))}
                  </List>
                  <Button component={Link} to="/login" variant="contained" fullWidth sx={{ mt: 3, bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' }, py: 1.5, borderRadius: 2 }}>
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Why Diversify & Why Now */}
      <Box sx={{ bgcolor: '#f8fffe', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <TrendingUpIcon sx={{ color: '#0d9488', fontSize: 32 }} />
                <Typography variant="h4" fontWeight={700}>Why Diversify?</Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Venture capital has historically been one of the highest-performing asset classes, yet most portfolios have zero allocation.
                Adding Israeli venture exposure provides geographic and sector diversification with access to a world-leading innovation ecosystem.
                Israel produces more startups per capita than any other nation, creating consistent opportunities for outsized returns.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <AccessTimeIcon sx={{ color: '#0d9488', fontSize: 32 }} />
                <Typography variant="h4" fontWeight={700}>Why Now?</Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Israeli tech is entering a new growth cycle driven by AI, cybersecurity, climate tech, and digital health.
                Valuations have reset, creating attractive entry points for new investors.
                With iAngels' decade of experience and 110+ portfolio companies, there has never been a better time to gain structured access to this ecosystem.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>Ready to Invest?</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Join 20,000+ members accessing Israel's top venture opportunities.
        </Typography>
        <Button component={Link} to="/login" variant="contained" size="large" sx={{ bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' }, px: 6, py: 1.5, borderRadius: 2 }}>
          Sign Up Now
        </Button>
      </Container>
    </Box>
  );
}
